// filepath: /Users/ephraim/yesodcopilot/client/src/pages/group/MembershipDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faArrowLeft, 
  faIdCard,
  faCheck,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { useGroup } from '../../context/GroupContext';
import groupService from '../../api/groupService';
import StripePaymentModal from '../../components/modals/StripePaymentModal';
import '../../styles/group/EventDetails.css'; // Reuse EventDetails styles for now

const MembershipDetails = () => {
  // URL params
  const { id: groupId, membershipId } = useParams();
  const navigate = useNavigate();
  
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribeLoading, setIsSubscribeLoading] = useState(false);
  
  // Handle image loading errors
  const [headerImageError, setHeaderImageError] = useState(false);

  // Direct fix for Supabase URLs with special characters
  const processImageUrl = (url) => {
    if (!url) return null;
    
    try {
      console.log('MembershipDetails - Original image URL:', url);
      
      // First try to create a valid URL object to see if it's parseable
      try {
        new URL(url);
      } catch (e) {
        console.error('Invalid URL format:', url);
        return null;
      }
      
      // Extract the base domain and path
      const urlObj = new URL(url);
      const baseDomain = `${urlObj.protocol}//${urlObj.host}`;
      const pathParts = urlObj.pathname.split('/');
      
      // Process each part of the path to properly encode
      const processedPathParts = pathParts.map((part, index) => {
        // Don't encode the first empty part (from the leading slash)
        if (index === 0 && part === '') return '';
        
        // For the filename (last part), ensure it's properly encoded
        if (index === pathParts.length - 1) {
          try {
            // First try to decode it in case it's already encoded but with issues
            const decodedPart = decodeURIComponent(part);
            // Then properly encode it
            return encodeURIComponent(decodedPart);
          } catch (e) {
            // If decoding fails, just encode as is
            return encodeURIComponent(part);
          }
        }
        
        // For path segments, just ensure they're properly encoded
        return encodeURIComponent(part);
      });
      
      // Build the URL back
      const processedPath = processedPathParts.join('/');
      let processedUrl = `${baseDomain}${processedPath}`;
      
      // Add query parameters if they exist
      if (urlObj.search) {
        processedUrl += urlObj.search;
      }
      
      console.log('MembershipDetails - Processed image URL:', processedUrl);
      
      // Add a cache buster to prevent browser caching issues
      const separator = processedUrl.includes('?') ? '&' : '?';
      return `${processedUrl}${separator}t=${Date.now()}`;
    } catch (error) {
      console.error('Error processing image URL:', error);
      return url;
    }
  };

  // Fetch membership details and subscriber info
  useEffect(() => {
    const fetchMembershipData = async () => {
      try {
        setLoading(true);
        
        // Fetch membership details
        // First get all memberships for the group
        const membershipsResponse = await groupService.getGroupMemberships(groupId);
        
        // Find the specific membership by ID
        const membershipData = Array.isArray(membershipsResponse) 
          ? membershipsResponse.find(m => m.id === membershipId)
          : null;
        
        if (!membershipData) {
          throw new Error('Membership not found');
        }
        
        setMembership(membershipData);
        
        // Check if user is subscribed to this membership
        try {
          const subscriptionResponse = await groupService.getUserMembershipSubscription(groupId, membershipId);
          if (subscriptionResponse && subscriptionResponse.data) {
            setIsSubscribed(true);
          }
        } catch (subsErr) {
          console.error('Error fetching subscription status:', subsErr);
          setIsSubscribed(false);
        }
        
        // Get subscribers for this membership (placeholder for now)
        // In a real implementation, you would fetch actual subscribers
        setSubscribers([]);  // Placeholder empty array
        setSubscribersCount(0);  // Placeholder count
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching membership details:', err);
        setError('Failed to load membership details. Please try again later.');
        setLoading(false);
      }
    };

    fetchMembershipData();
  }, [groupId, membershipId]);

  // Go back to group page
  const handleBack = () => {
    navigate(`/group/${groupId}`);
  };

  // Handle subscription toggle
  const handleSubscribe = async () => {
    try {
      setIsSubscribeLoading(true);
      
      if (isSubscribed) {
        // Already subscribed, so unsubscribe
        await groupService.cancelMembershipSubscription(groupId, membershipId);
        setIsSubscribed(false);
      } else {
        // Subscribe to the membership
        const response = await groupService.subscribeMembership(groupId, membershipId);
        if (response.success) {
          setIsSubscribed(true);
        }
      }
      
      // Refresh subscribers (placeholder for now)
      refreshSubscribers();
    } catch (error) {
      console.error('Error handling subscription:', error);
      if (window.showToast) {
        window.showToast('Error updating subscription status', 'error');
      }
    } finally {
      setIsSubscribeLoading(false);
    }
  };

  // Refresh subscribers list (placeholder function)
  const refreshSubscribers = async () => {
    // In a real application, you would fetch updated subscriber information
    // This is a placeholder that maintains the current implementation
    try {
      setSubscribers([]); // Placeholder
      setSubscribersCount(0); // Placeholder
    } catch (err) {
      console.error('Error refreshing subscribers:', err);
    }
  };

  // Open sponsor modal
  const openSponsorModal = () => {
    setShowSponsorModal(true);
  };

  // Close sponsor modal
  const handleCloseSponsorModal = () => {
    setShowSponsorModal(false);
  };

  // Process benefits array
  const membershipBenefits = membership && Array.isArray(membership.benefits) 
    ? membership.benefits 
    : membership && typeof membership.benefits === 'string'
      ? JSON.parse(membership.benefits)
      : [];

  return (
    <div className="event-details-page">
      {loading ? (
        <div className="loading-container">Loading membership details...</div>
      ) : error ? (
        <div className="error-container">{error}</div>
      ) : membership ? (
        <>
          {/* Membership header with cover image */}
          <div className="event-details-header">
            <button className="back-button" onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} /> Details
            </button>
            
            {membership.cover_image_url && !headerImageError ? (
              <img 
                className="event-header-img"
                src={processImageUrl(membership.cover_image_url)}
                alt={membership.name}
                onError={() => setHeaderImageError(true)}
              />
            ) : (
              <div 
                className="event-header-placeholder"
                style={{
                  backgroundColor: '#6a5acd', // Slate blue for memberships
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '4rem'
                }}
              >
                <FontAwesomeIcon icon={faIdCard} />
              </div>
            )}
            
            <div className="event-header-overlay"></div>
          </div>
          
          {/* Subscribers section */}
          <div className="event-attendees-container">
            <div className="attendees-section">
              <div className="attendees-avatars">
                {/* Show 3 gray placeholder icons when no subscribers */}
                {subscribers.length > 0 ? (
                  subscribers.map((subscriber, index) => (
                    <div 
                      key={index}
                      className="attendee-avatar"
                      style={{
                        backgroundImage: subscriber.profile_image ? 
                          `url(${processImageUrl(subscriber.profile_image)})` : 
                          'none'
                      }}
                    ></div>
                  ))
                ) : (
                  // Three gray placeholders when no subscribers
                  <>
                    <div className="attendee-avatar empty-avatar"></div>
                    <div className="attendee-avatar empty-avatar"></div>
                    <div className="attendee-avatar empty-avatar"></div>
                  </>
                )}
              </div>
              <div className="attendees-count">
                {/* Always show the count, even when it's 0 */}
                {`${subscribersCount} members`}
              </div>
            </div>
            
            <button 
              className="rsvp-btn"
              onClick={handleSubscribe}
              disabled={isSubscribeLoading}
            >
              {isSubscribeLoading ? '...' : (isSubscribed ? 'Joined' : 'Join')}
            </button>
          </div>
          
          {/* Membership details content */}
          <div className="event-details-content">
            <h1 className="event-title">{membership.name}</h1>
            
            <div className="event-meta">
              {/* Price section */}
              <div className="event-date-time">
                <div className="event-icon-container">
                  <FontAwesomeIcon icon={faUserCheck} />
                </div>
                <div className="event-info-container">
                  <div className="event-date">
                    ${membership.price}{membership.interval === 'one-time' ? '' : (membership.recurring && `/${membership.interval.charAt(0).toUpperCase() + membership.interval.slice(1)}`)}
                  </div>
                  <div className="event-time">
                    {membership.featured && <span className="featured-label">Featured Plan</span>}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Description section with benefits */}
            <div className="event-description-section">
              <h2 className="event-description-title">About</h2>
              <div className="event-description">
                {membership.description}
                
                {/* Benefits list under the description */}
                {membershipBenefits && membershipBenefits.length > 0 && (
                  <ul className="membership-benefits-list" style={{ marginTop: '20px' }}>
                    {membershipBenefits.map((benefit, index) => (
                      <li key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        marginBottom: '10px'
                      }}>
                        <FontAwesomeIcon 
                          icon={faCheck} 
                          style={{ 
                            color: '#4CAF50', 
                            marginRight: '10px',
                            marginTop: '4px'
                          }} 
                        />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          
          {/* Sponsor button */}
          <div className="sponsor-button-container">
            <button 
              className="sponsor-button-simple"
              onClick={openSponsorModal}
              aria-label="Sponsor this membership"
            >
              SPONSOR THIS MEMBERSHIP
            </button>
          </div>
          
          {/* Stripe Payment Modal */}
          <StripePaymentModal
            isOpen={showSponsorModal}
            onClose={handleCloseSponsorModal}
            groupId={groupId}
            eventId={membershipId}
            eventTitle={membership?.title || 'Membership'}
            isGroupDonation={true}
            onPaymentSuccess={(data) => {
              console.log('Payment successful:', data);
              refreshSubscribers();
            }}
          />
        </>
      ) : (
        <div className="error-container">Membership not found</div>
      )}
    </div>
  );
};

export default MembershipDetails;